import { useCallback, useState } from 'react';

import { Flex } from '@joinrepublic/dls-ui-kit';
import CN from 'clsx';

import { IBannerTypeEnum } from '@r-client/data/graphql-types';
import { useAnalytics } from '@r-client/shared/data/analytics';
import { Box, Button, Text } from '@r-client/shared/ui/core';
import { CrossMedium, SvgIconLight, Warning } from '@r-client/shared/ui/icons';
import { useIsUnmounted } from '@r-client/shared/util/core';

import styles from './two-factor-reminder-banner.module.scss';

const SETTINGS_ACCOUNT_PATH = '/settings/account';

export interface ITwoFactorReminderBannerProps {
  deadline: string;
  onClose: (bannerType: IBannerTypeEnum) => Promise<void>;
}

function formatDeadline(deadline: string) {
  const date = new Date(deadline);

  if (Number.isNaN(date.getTime())) {
    return deadline;
  }

  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

export const TwoFactorReminderBanner = ({
  deadline,
  onClose,
}: ITwoFactorReminderBannerProps) => {
  const analytics = useAnalytics();
  const [bannerIsDismissing, setBannerIsDismissing] = useState(false);
  const isUnmounted = useIsUnmounted();

  const handleSetupClick = () => {
    analytics.track({
      name: 'website_banner_clicked',
      params: {
        banner_name: 'TwoFactorReminderBanner',
        button_name: 'set_up_now',
      },
    });
  };

  const handleClose = useCallback(async () => {
    setBannerIsDismissing(true);
    try {
      await onClose(IBannerTypeEnum.TwoFactorReminder);
    } finally {
      if (!isUnmounted.current) {
        setBannerIsDismissing(false);
      }
    }
  }, [onClose, isUnmounted]);

  return (
    <Flex
      justifyContent="space-between"
      alignItems="center"
      direction={{ sm: 'column', lg: 'row' }}
      className={styles.messageContainer}
    >
      <Flex alignItems="center" p={{ sm: '1brm 0 0', lg: '1brm 0' }}>
        <SvgIconLight
          height={16}
          icon={Warning}
          iconClassName={styles.warningIcon}
        />
        <Text
          weight="400"
          className={styles.text}
          p={{ sm: '0 0 0 0.25brm', lg: '0 0 0 0.5brm' }}
        >
          Two-step verification becomes required on {formatDeadline(deadline)}.
          <Text as="span" className={styles.hiddenXs}>
            {' '}
            Set it up now so you can continue logging in without interruption.
          </Text>
        </Text>
      </Flex>
      <Flex alignItems="center">
        <Box m={{ sm: '0.5brm', lg: '0.5brm 0 0.5brm 1.5brm' }}>
          <Button
            href={SETTINGS_ACCOUNT_PATH}
            size="small"
            onClick={handleSetupClick}
          >
            Set up now
          </Button>
        </Box>
        <SvgIconLight
          icon={CrossMedium}
          className={CN(styles.closeIcon, {
            [styles.closeIconDisabled]: bannerIsDismissing,
          })}
          dataTestId="close-banner"
          label="Dismiss reminder"
          isDisabled={bannerIsDismissing}
          onClick={handleClose}
        />
      </Flex>
    </Flex>
  );
};
